import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Sparkles, Plus, LogOut, Copy, Trash2, KeyRound, ExternalLink, Store, Lock,
  CreditCard, CheckCircle2, AlertTriangle, ShieldCheck, RefreshCw,
  Mail, Phone, MessageSquare, ChevronRight, Download, Upload,
} from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const CATEGORIES = ["Cafe", "Restaurant", "Salon & Spa", "Retail", "Fitness", "Bakery", "Other"];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("businesses");
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [billingFor, setBillingFor] = useState(null);
  const [smsFor, setSmsFor] = useState(null);
  const [importFor, setImportFor] = useState(null);

  const exportBusiness = async (row) => {
    try {
      const { data } = await api.get(`/admin/businesses/${row.business.id}/export`);
      const slug = (row.business.slug || row.business.name || "business").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sparkloop-${slug}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${row.business.name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const [changePwOpen, setChangePwOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/businesses");
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    try {
      setTicketsLoading(true);
      const { data } = await api.get("/admin/support-tickets");
      setTickets(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "support") loadTickets();
  }, [tab]);

  const openTicketsCount = tickets.filter((t) => t.status === "open").length;

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-[#e5e5eb] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-[#002fa7] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-extrabold">SparkLoop</span>
            <span className="text-xs uppercase tracking-[0.2em] text-[#002fa7] font-semibold bg-[#002fa7]/10 rounded-full px-2.5 py-1">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5a5a65] hidden sm:block">{user?.username}</span>
            <button
              data-testid="admin-change-pw-btn"
              onClick={() => setChangePwOpen(true)}
              className="flex items-center gap-2 text-sm text-[#5a5a65] hover:text-[#0a0a0a]"
              title="Change password"
            >
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Password</span>
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-[#5a5a65] hover:text-[#0a0a0a]"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Tab switcher */}
        <div className="flex items-center gap-2 mb-8 border-b border-[#e5e5eb]">
          <TabButton active={tab === "businesses"} onClick={() => setTab("businesses")} testid="admin-tab-businesses">
            Businesses
          </TabButton>
          <TabButton active={tab === "support"} onClick={() => setTab("support")} testid="admin-tab-support">
            Support
            {openTicketsCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-[#e62b3a] text-white text-[10px] font-bold px-1.5">
                {openTicketsCount}
              </span>
            )}
          </TabButton>
        </div>

        {tab === "businesses" && (
          <>
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">
              Businesses
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">
              {rows.length} on the platform.
            </h1>
          </div>
          <Button
            data-testid="admin-create-btn"
            onClick={() => setCreateOpen(true)}
            className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> New business
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-sm text-[#5a5a65] py-20">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-[#e5e5eb] rounded-2xl p-16 text-center">
            <div className="w-12 h-12 rounded-full bg-[#f0f0f4] flex items-center justify-center mx-auto mb-4">
              <Store className="w-6 h-6 text-[#002fa7]" />
            </div>
            <h3 className="font-display text-2xl font-extrabold mb-2">No businesses yet</h3>
            <p className="text-[#5a5a65] mb-6">Add your first business and hand the merchant their login.</p>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full"
            >
              Add business
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-[#e5e5eb] rounded-2xl overflow-hidden">
            <Table data-testid="admin-businesses-table">
              <TableHeader>
                <TableRow className="bg-[#f9f9fb] hover:bg-[#f9f9fb]">
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Business</TableHead>
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Owner</TableHead>
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Rate</TableHead>
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Customers</TableHead>
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Billing</TableHead>
                  <TableHead className="uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Kiosk URL</TableHead>
                  <TableHead className="text-right uppercase tracking-[0.16em] text-[11px] font-semibold text-[#5a5a65]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const kioskUrl = `${window.location.origin}/kiosk/${r.business.slug}`;
                  return (
                    <TableRow key={r.business.id} className="hover:bg-[#f9f9fb]">
                      <TableCell>
                        <div className="font-semibold">{r.business.name}</div>
                        <div className="text-xs text-[#5a5a65]">{r.business.category}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">{r.owner?.name || "—"}</div>
                        <div className="text-xs text-[#5a5a65] font-mono-data">@{r.owner?.username}</div>
                      </TableCell>
                      <TableCell className="font-mono-data text-sm">
                        {r.business.points_per_dollar} pts / $1
                      </TableCell>
                      <TableCell className="font-mono-data">{r.customers}</TableCell>
                      <TableCell>
                        <BillingBadge billing={r.billing} />
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => copy(kioskUrl)}
                          className="text-xs font-mono-data text-[#002fa7] hover:underline flex items-center gap-1"
                          data-testid={`copy-kiosk-${r.business.id}`}
                        >
                          /kiosk/{r.business.slug} <Copy className="w-3 h-3" />
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(kioskUrl, "_blank")}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`open-kiosk-${r.business.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setBillingFor(r)}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`billing-${r.business.id}`}
                            title="Billing"
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => exportBusiness(r)}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`export-${r.business.id}`}
                            title="Export data (JSON download)"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setImportFor(r)}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`import-${r.business.id}`}
                            title="Import data"
                          >
                            <Upload className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSmsFor(r)}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`sms-${r.business.id}`}
                            title="SMS Marketing"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setResetFor(r)}
                            className="rounded-full text-[#5a5a65]"
                            data-testid={`reset-pw-${r.business.id}`}
                            title="Reset password"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteFor(r)}
                            className="rounded-full text-[#5a5a65] hover:text-[#c00]"
                            data-testid={`delete-biz-${r.business.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
          </>
        )}

        {tab === "support" && (
          <SupportTicketsView
            tickets={tickets}
            loading={ticketsLoading}
            onReload={loadTickets}
          />
        )}
      </main>

      <CreateBusinessDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />
      <ResetPasswordDialog
        row={resetFor}
        onClose={() => setResetFor(null)}
      />
      <DeleteBusinessDialog
        row={deleteFor}
        onClose={() => setDeleteFor(null)}
        onDeleted={load}
      />
      <BillingDialog
        row={billingFor}
        onClose={() => setBillingFor(null)}
        onChanged={load}
      />
      <SmsConfigDialog
        row={smsFor}
        onClose={() => setSmsFor(null)}
      />
      <ImportDataDialog
        row={importFor}
        onClose={() => setImportFor(null)}
        onDone={load}
      />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}

function CreateBusinessDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    business_name: "", category: "Cafe", description: "",
    points_per_dollar: 10,
    plan: "monthly",
    monthly_price_usd: "",
    owner_name: "", owner_username: "", owner_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const reset = () => {
    setForm({ business_name: "", category: "Cafe", description: "", points_per_dollar: 10, plan: "monthly", monthly_price_usd: "", owner_name: "", owner_username: "", owner_password: "" });
    setCreated(null);
  };

  const close = () => { reset(); onOpenChange(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.owner_username.trim() || !form.owner_password) {
      toast.error("Fill in all required fields");
      return;
    }
    const price = Number(form.monthly_price_usd);
    if (!price || price <= 0) {
      toast.error("Enter the monthly price you negotiated with this merchant");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/businesses", {
        business_name: form.business_name.trim(),
        category: form.category,
        description: form.description,
        points_per_dollar: Number(form.points_per_dollar) || 0,
        monthly_price_usd: Number(form.monthly_price_usd) || 0,
        billing_period_days: form.plan === "annual" ? 365 : 30,
        owner_name: form.owner_name.trim() || form.owner_username,
        owner_username: form.owner_username.trim().toLowerCase(),
        owner_password: form.owner_password,
      });
      setCreated({
        ...data,
        kiosk_url: `${window.location.origin}/kiosk/${data.business.slug}`,
        username: form.owner_username.trim().toLowerCase(),
        password: form.owner_password,
      });
      await onCreated?.();
      toast.success("Business created");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent data-testid="create-biz-dialog" className="sm:max-w-lg">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                {created.business.name} is live
              </DialogTitle>
              <DialogDescription>
                Share these with the merchant. They can use them forever.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <CredentialRow label="Merchant login URL" value={`${window.location.origin}/login`} />
              <CredentialRow label="Username" value={created.username} />
              <CredentialRow label="Password" value={created.password} />
              <CredentialRow label="Customer kiosk URL" value={created.kiosk_url} />
            </div>
            <DialogFooter>
              <Button onClick={close} className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">Done</Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">New business</DialogTitle>
              <DialogDescription>Create the business profile and the merchant's login.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <FieldLabel>Business name</FieldLabel>
              <Input data-testid="biz-name-input" value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Rosa's Pizzeria"
                className="h-12 rounded-xl border-[#e5e5eb]" required />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Category</FieldLabel>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="biz-category-select" className="h-12 rounded-xl border-[#e5e5eb]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel>Points per $1</FieldLabel>
                  <Input data-testid="biz-ppd-input" type="number" step="0.1" min="0"
                    value={form.points_per_dollar}
                    onChange={(e) => setForm({ ...form, points_per_dollar: e.target.value })}
                    className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" required />
                </div>
              </div>
              <div>
                <FieldLabel>Subscription plan</FieldLabel>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <button
                    type="button"
                    data-testid="plan-monthly-btn"
                    onClick={() => setForm((f) => ({ ...f, plan: "monthly" }))}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${
                      form.plan === "monthly"
                        ? "border-[#002fa7] bg-[#f0f4ff]"
                        : "border-[#e5e5eb] hover:border-[#0a0a0a]"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#5a5a65] mb-1">Monthly</div>
                    <div className="font-display text-lg font-extrabold">Billed every 30 days</div>
                  </button>
                  <button
                    type="button"
                    data-testid="plan-annual-btn"
                    onClick={() => setForm((f) => ({ ...f, plan: "annual" }))}
                    className={`rounded-xl border-2 p-3 text-left transition-all ${
                      form.plan === "annual"
                        ? "border-[#002fa7] bg-[#f0f4ff]"
                        : "border-[#e5e5eb] hover:border-[#0a0a0a]"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#5a5a65] mb-1">Annual</div>
                    <div className="font-display text-lg font-extrabold">Billed once a year</div>
                  </button>
                </div>
                <FieldLabel>Price per month (USD)</FieldLabel>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65] font-mono-data">$</span>
                  <Input
                    data-testid="biz-monthly-price-input"
                    type="number" step="0.01" min="0"
                    value={form.monthly_price_usd}
                    onChange={(e) => setForm({ ...form, monthly_price_usd: e.target.value })}
                    placeholder="Enter the rate you negotiated"
                    className="h-12 pl-8 rounded-xl border-[#e5e5eb] font-mono-data"
                    required
                  />
                </div>
                <p className="text-[11px] text-[#5a5a65] mt-1.5">
                  {form.plan === "annual"
                    ? `Per-month rate. Charged annually as $${(Number(form.monthly_price_usd || 0) * 12).toFixed(2)}.`
                    : "Charged monthly. 14-day free trial applies automatically."}
                </p>
              </div>
              <div>
                <FieldLabel>Description</FieldLabel>
                <Textarea data-testid="biz-description-input" rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-xl border-[#e5e5eb]" />
              </div>
              <div className="h-px bg-[#e5e5eb] my-2" />
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">
                Merchant credentials
              </div>
              <FieldLabel>Owner name</FieldLabel>
              <Input data-testid="biz-owner-name-input" value={form.owner_name}
                onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                placeholder="Jane Smith"
                className="h-12 rounded-xl border-[#e5e5eb]" required />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Username</FieldLabel>
                  <Input data-testid="biz-owner-username-input" value={form.owner_username}
                    onChange={(e) => setForm({ ...form, owner_username: e.target.value })}
                    placeholder="jane"
                    className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" required />
                </div>
                <div>
                  <FieldLabel>Password</FieldLabel>
                  <Input data-testid="biz-owner-password-input" type="text" value={form.owner_password}
                    onChange={(e) => setForm({ ...form, owner_password: e.target.value })}
                    placeholder="make it strong"
                    className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" required />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close} className="rounded-full border-[#e5e5eb]">
                Cancel
              </Button>
              <Button data-testid="biz-create-submit-btn" type="submit" disabled={submitting}
                className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">
                {submitting ? "Creating…" : "Create business"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ row, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setPw(""); }, [row]);
  if (!row) return null;
  const submit = async () => {
    if (pw.length < 4) return toast.error("Min 4 characters");
    setBusy(true);
    try {
      await api.post(`/admin/businesses/${row.business.id}/reset-password`, { new_password: pw });
      toast.success(`Password updated for @${row.owner?.username}`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Reset password</DialogTitle>
          <DialogDescription>New password for @{row.owner?.username} ({row.business.name}).</DialogDescription>
        </DialogHeader>
        <Input data-testid="reset-pw-input" value={pw} onChange={(e) => setPw(e.target.value)}
          placeholder="new password" className="h-12 rounded-xl border-[#e5e5eb] font-mono-data" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full border-[#e5e5eb]">Cancel</Button>
          <Button data-testid="reset-pw-submit" disabled={busy} onClick={submit}
            className="bg-[#002fa7] hover:bg-[#002585] text-white rounded-full">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBusinessDialog({ row, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/businesses/${row.business.id}`);
      toast.success("Deleted");
      await onDeleted?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Delete {row.business.name}?</DialogTitle>
          <DialogDescription>
            This removes the business, its merchant account, all customers, rewards, and history. Irreversible.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full border-[#e5e5eb]">Cancel</Button>
          <Button data-testid="delete-biz-confirm" disabled={busy} onClick={submit}
            className="bg-[#c00] hover:bg-[#a00] text-white rounded-full">
            {busy ? "Deleting…" : "Delete forever"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }) {
  return (
    <label className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 block">
      {children}
    </label>
  );
}

function CredentialRow({ label, value }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="bg-[#f9f9fb] border border-[#e5e5eb] rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5a5a65] font-semibold mb-1">{label}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono-data text-sm truncate">{value}</span>
        <button onClick={copy} className="text-[#5a5a65] hover:text-[#002fa7] flex-shrink-0">
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const BILLING_LABELS = {
  trialing: { label: "Trial", cls: "bg-[#fff4d6] text-[#7a5a00]", Icon: CreditCard },
  active: { label: "Active", cls: "bg-[#dcf6e2] text-[#1a6b34]", Icon: CheckCircle2 },
  past_due: { label: "Past due", cls: "bg-[#fde0e0] text-[#a01010]", Icon: AlertTriangle },
  manual_bypass: { label: "Manual", cls: "bg-[#e6e6ff] text-[#1a1a8a]", Icon: ShieldCheck },
  canceled: { label: "Canceled", cls: "bg-[#eeeeee] text-[#5a5a65]", Icon: AlertTriangle },
};

function BillingBadge({ billing }) {
  if (!billing) return <span className="text-xs text-[#5a5a65]">—</span>;
  const meta = BILLING_LABELS[billing.status] || BILLING_LABELS.trialing;
  const Icon = meta.Icon;
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider w-fit ${meta.cls}`}>
        <Icon className="w-3 h-3" />
        {meta.label}
      </span>
      {billing.days_left !== null && billing.days_left !== undefined && (
        <span className="text-[11px] text-[#5a5a65] font-mono-data">
          {billing.days_left}d left
        </span>
      )}
    </div>
  );
}

function ImportDataDialog({ row, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mode, setMode] = useState("merge");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const open = !!row;

  useEffect(() => {
    if (!open) {
      setFile(null);
      setParsed(null);
      setMode("merge");
      setError(null);
    }
  }, [open]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    try {
      const text = await f.text();
      const data = JSON.parse(text);
      if (![1, 2].includes(data.version)) throw new Error("Not a SparkLoop export (version mismatch).");
      setParsed(data);
    } catch (err) {
      setParsed(null);
      setError(err.message || "Couldn't read this file.");
    }
  };

  const submit = async () => {
    if (!parsed) return;
    const sourceName = parsed.source_business?.name || parsed.business?.name || "(unknown)";
    if (!window.confirm(
      `Import customers from "${sourceName}" into "${row.business.name}"?\n\n` +
      `Mode: ${mode === "replace" ? "REPLACE — wipes target's existing customer list (loyalty cards) first" : "MERGE — keeps existing customers, only adds/updates from the file"}`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/businesses/${row.business.id}/import`, {
        data: parsed,
        mode,
      });
      const c = data.counts;
      toast.success(`Imported: ${c.customers_new} new customers, ${c.cards} loyalty cards`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
            Import customers · {row.business.name}
          </DialogTitle>
          <DialogDescription>
            Load a SparkLoop customer-list export into this business. Imports customers and their loyalty cards (points, visits, SMS opt-in). Does not touch rewards, ads, billing, or settings.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-2">
              Choose export file (.json)
            </div>
            <input
              data-testid="admin-import-file"
              type="file"
              accept=".json,application/json"
              onChange={onPick}
              className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#002fa7] file:text-white file:font-semibold hover:file:bg-[#002585]"
            />
            {error && <div className="mt-2 text-xs text-[#a01010] font-semibold">{error}</div>}
          </div>

          {parsed && (
            <div className="bg-[#f9f9fb] border border-[#e5e5eb] rounded-2xl p-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#5a5a65]">Source</span><span className="font-semibold">{parsed.source_business?.name || parsed.business?.name}</span></div>
              <div className="flex justify-between"><span className="text-[#5a5a65]">Customers</span><span className="font-mono-data">{(parsed.customers || []).length}</span></div>
              <div className="flex justify-between"><span className="text-[#5a5a65]">Loyalty cards</span><span className="font-mono-data">{(parsed.loyalty_cards || []).length}</span></div>
              <div className="flex justify-between"><span className="text-[#5a5a65]">Exported</span><span className="font-mono-data text-xs">{parsed.exported_at ? new Date(parsed.exported_at).toLocaleString() : "—"}</span></div>
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-2">Mode</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="admin-import-mode-merge"
                onClick={() => setMode("merge")}
                className={`text-left p-3 rounded-xl border-2 transition ${
                  mode === "merge" ? "border-[#002fa7] bg-[#002fa7]/5" : "border-[#e5e5eb] hover:border-[#a0a0a8]"
                }`}
              >
                <div className="font-semibold text-sm">Merge</div>
                <div className="text-[11px] text-[#5a5a65] mt-0.5">Adds/updates from the file. Existing data kept.</div>
              </button>
              <button
                data-testid="admin-import-mode-replace"
                onClick={() => setMode("replace")}
                className={`text-left p-3 rounded-xl border-2 transition ${
                  mode === "replace" ? "border-[#a01010] bg-[#fde0e0]" : "border-[#e5e5eb] hover:border-[#a0a0a8]"
                }`}
              >
                <div className="font-semibold text-sm">Replace</div>
                <div className="text-[11px] text-[#5a5a65] mt-0.5">Wipes target's customer list first, then loads file.</div>
              </button>
            </div>
          </div>
        </div>
        <DialogFooter className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            data-testid="admin-import-submit"
            onClick={submit}
            disabled={!parsed || busy}
            className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white"
          >
            {busy ? "Importing…" : `Import ${mode === "replace" ? "(replace)" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SmsConfigDialog({ row, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [editing, setEditing] = useState(false);

  const open = !!row;
  const bizId = row?.business?.id;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setEditing(false);
    api
      .get(`/admin/businesses/${bizId}/sms-config`)
      .then(({ data }) => {
        if (cancelled) return;
        setCfg(data);
        setForm({
          account_sid: data.account_sid || "",
          auth_token: "",
          from_number: data.from_number || "",
        });
        setEditing(!data.configured);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, bizId]);

  const save = async () => {
    if (!form.account_sid.trim() || !form.auth_token.trim() || !form.from_number.trim()) {
      toast.error("All three fields are required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/businesses/${bizId}/sms-config`, form);
      toast.success("SMS credentials verified and saved");
      const { data } = await api.get(`/admin/businesses/${bizId}/sms-config`);
      setCfg(data);
      setForm({ account_sid: data.account_sid, auth_token: "", from_number: data.from_number });
      setEditing(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Disconnect Twilio for ${row.business.name}? They won't be able to send SMS blasts until reconnected.`)) return;
    try {
      await api.delete(`/admin/businesses/${bizId}/sms-config`);
      toast.success("Disconnected");
      const { data } = await api.get(`/admin/businesses/${bizId}/sms-config`);
      setCfg(data);
      setForm({ account_sid: "", auth_token: "", from_number: "" });
      setEditing(true);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendTest = async () => {
    if (!testPhone.replace(/\D/g, "")) return;
    setTesting(true);
    try {
      await api.post(`/admin/businesses/${bizId}/sms-config/test`, { to: testPhone });
      toast.success("Test SMS sent");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTesting(false); }
  };

  if (!open) return null;
  const webhookFullUrl = `${window.location.origin}${cfg?.webhook_url || ""}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
            SMS Marketing · {row.business.name}
          </DialogTitle>
          <DialogDescription>
            Configure Twilio credentials on this merchant's behalf. They'll be able to send SMS blasts as soon as you save.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm uppercase tracking-[0.2em] text-[#5a5a65]">Loading…</div>
        ) : !editing && cfg?.configured ? (
          <div className="space-y-4">
            <div className="bg-[#dcf6e2] border border-[#a8dfb8] rounded-2xl p-3 text-sm text-[#1a6b34] flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Connected · verified {cfg.verified_at ? new Date(cfg.verified_at).toLocaleDateString() : ""}
            </div>
            <KV label="Account SID" value={cfg.account_sid} />
            <KV label="Auth token" value={cfg.auth_token_masked} />
            <KV label="Sending number" value={cfg.from_number} />
            <div className="pt-3 border-t border-[#e5e5eb]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">
                STOP webhook (must be set in Twilio for compliance)
              </div>
              <div className="font-mono-data text-xs break-all bg-[#f9f9fb] px-3 py-2 rounded-lg">{webhookFullUrl}</div>
            </div>
            <div className="pt-3 border-t border-[#e5e5eb] space-y-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65]">Send a test SMS</div>
              <div className="flex gap-2">
                <Input
                  data-testid="admin-sms-test-phone"
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="flex-1"
                />
                <Button
                  data-testid="admin-sms-test-send"
                  onClick={sendTest}
                  disabled={testing || !testPhone.trim()}
                  className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white"
                >
                  {testing ? "Sending…" : "Send"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-[#fff4d6] border border-[#f0d890] rounded-xl p-3 text-xs text-[#7a5a00] leading-snug">
              Use this merchant's Twilio Account SID, Auth Token, and a phone number they own in Twilio. We'll verify with Twilio before saving.
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">Account SID</div>
              <Input
                data-testid="admin-sms-sid"
                value={form.account_sid}
                onChange={(e) => setForm({ ...form, account_sid: e.target.value })}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="font-mono-data"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">Auth Token</div>
              <Input
                data-testid="admin-sms-token"
                type="password"
                value={form.auth_token}
                onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
                placeholder="(paste merchant's Twilio auth token)"
                className="font-mono-data"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65] mb-1.5">Sending phone number</div>
              <Input
                data-testid="admin-sms-from"
                value={form.from_number}
                onChange={(e) => setForm({ ...form, from_number: e.target.value })}
                placeholder="+15551234567"
                className="font-mono-data"
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2 sm:justify-between">
          {cfg?.configured && !editing ? (
            <>
              <Button variant="outline" onClick={remove} className="rounded-full border-[#ff3355] text-[#ff3355] hover:bg-[#ff3355] hover:text-white">
                Disconnect
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="rounded-full">Close</Button>
                <Button onClick={() => setEditing(true)} className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white">
                  Update credentials
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button data-testid="admin-sms-save" onClick={save} disabled={saving} className="bg-[#002fa7] hover:bg-[#002585] rounded-full text-white">
                {saving ? "Verifying…" : "Verify & save"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5a5a65]">{label}</div>
      <div className="font-mono-data text-xs text-[#0a0a0a] text-right break-all">{value}</div>
    </div>
  );
}


function BillingDialog({ row, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState("");
  const [daysLeftDraft, setDaysLeftDraft] = useState("");

  useEffect(() => {
    if (!row) return;
    setData(null);
    setMonths(1);
    setNote("");
    setEditingPrice(false);
    setDaysLeftDraft("");
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/businesses/${row.business.id}/billing`);
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [row]);

  if (!row) return null;

  const savePrice = async () => {
    const v = Number(priceDraft);
    if (!v || v <= 0) {
      toast.error("Enter a valid price");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/businesses/${row.business.id}`, {
        monthly_price_usd: v,
      });
      const { data: refreshed } = await api.get(`/admin/businesses/${row.business.id}/billing`);
      setData(refreshed);
      setEditingPrice(false);
      toast.success(`Monthly price set to $${v.toFixed(2)}`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const switchPlan = async (toAnnual) => {
    setBusy(true);
    try {
      // Keep the merchant's current monthly_price_usd — admin must adjust manually if needed.
      await api.patch(`/admin/businesses/${row.business.id}`, {
        billing_period_days: toAnnual ? 365 : 30,
      });
      const { data: refreshed } = await api.get(`/admin/businesses/${row.business.id}/billing`);
      setData(refreshed);
      toast.success(toAnnual ? "Switched to Annual billing" : "Switched to Monthly billing");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleBypass = async () => {
    setBusy(true);
    try {
      const { data: b } = await api.post(
        `/admin/businesses/${row.business.id}/billing/bypass`,
        { enabled: !data.billing.manual_bypass, note: note || "" },
      );
      setData((d) => ({ ...d, billing: b }));
      toast.success(b.manual_bypass ? "Billing bypass enabled" : "Bypass removed");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      const { data: b } = await api.post(
        `/admin/businesses/${row.business.id}/billing/mark-paid`,
        { months: Number(months) || 1, note },
      );
      const { data: refreshed } = await api.get(`/admin/businesses/${row.business.id}/billing`);
      setData(refreshed);
      toast.success(`Marked paid for ${months} month${months > 1 ? "s" : ""}`);
      setNote("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setDaysLeft = async () => {
    const n = Number(daysLeftDraft);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Enter a whole number of days (0 or more)");
      return;
    }
    if (!window.confirm(
      `Set "${row.business.name}" to have ${n} day${n === 1 ? "" : "s"} left in their current period?`
    )) return;
    setBusy(true);
    try {
      await api.post(
        `/admin/businesses/${row.business.id}/billing/set-days-left`,
        { days_left: Math.floor(n), note },
      );
      const { data: refreshed } = await api.get(`/admin/businesses/${row.business.id}/billing`);
      setData(refreshed);
      toast.success(`Days left set to ${Math.floor(n)}`);
      setDaysLeftDraft("");
      setNote("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const syncStripe = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(
        `/admin/businesses/${row.business.id}/billing/sync`,
      );
      const { data: refreshed } = await api.get(`/admin/businesses/${row.business.id}/billing`);
      setData(refreshed);
      if (res.newly_paid > 0) {
        toast.success(`Synced ${res.synced} session${res.synced === 1 ? "" : "s"} — ${res.newly_paid} new payment${res.newly_paid === 1 ? "" : "s"} applied`);
      } else if (res.synced > 0) {
        toast.success(`Synced ${res.synced} session${res.synced === 1 ? "" : "s"} — no new payments`);
      } else {
        toast.info("Nothing to sync");
      }
      if (res.errors?.length) {
        toast.error(`${res.errors.length} session${res.errors.length === 1 ? "" : "s"} failed to sync`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const b = data?.billing;
  const txns = data?.transactions || [];

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-billing-dialog" className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
            Billing — {row.business.name}
          </DialogTitle>
          <DialogDescription>
            Stripe subscription, manual bypass for cash/ACH, and payment history.
          </DialogDescription>
        </DialogHeader>

        {loading || !b ? (
          <div className="py-10 text-center text-sm text-[#5a5a65]">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatTile label="Status">
                <BillingBadge billing={b} />
              </StatTile>
              <StatTile label={`Plan · ${b.plan_label}`}>
                {editingPrice ? (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5a65] font-mono-data text-sm">$</span>
                      <Input
                        data-testid="edit-price-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceDraft}
                        onChange={(e) => setPriceDraft(e.target.value)}
                        className="h-10 pl-7 rounded-xl border-[#e5e5eb] font-mono-data"
                        autoFocus
                      />
                    </div>
                    <Button
                      data-testid="save-price-btn"
                      size="sm"
                      disabled={busy}
                      onClick={savePrice}
                      className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white"
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingPrice(false)}
                      className="rounded-full text-[#5a5a65]"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-2xl font-extrabold">
                        ${b.monthly_price_usd?.toFixed(2)}
                      </span>
                      <span className="text-xs text-[#5a5a65]">/mo</span>
                      <button
                        data-testid="edit-price-btn"
                        onClick={() => {
                          setPriceDraft(String(b.monthly_price_usd ?? ""));
                          setEditingPrice(true);
                        }}
                        className="ml-auto text-[11px] uppercase tracking-wider font-semibold text-[#002fa7] hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                    <div className="text-[11px] text-[#5a5a65] mt-1">
                      {b.billing_period_days >= 180
                        ? `$${(b.monthly_price_usd * 12).toFixed(2)} billed yearly`
                        : "Billed monthly"}
                    </div>
                    <button
                      data-testid="switch-plan-btn"
                      onClick={() => switchPlan(b.billing_period_days < 180)}
                      disabled={busy}
                      className="mt-2 text-[11px] uppercase tracking-wider font-semibold text-[#002fa7] hover:underline disabled:opacity-50"
                    >
                      Switch to {b.billing_period_days >= 180 ? "Monthly" : "Annual"} →
                    </button>
                  </div>
                )}
              </StatTile>
              <StatTile label="Next charge / period end">
                <span className="font-mono-data text-sm">
                  {b.current_period_end
                    ? new Date(b.current_period_end).toLocaleDateString()
                    : "—"}
                </span>
              </StatTile>
              <StatTile label="Last paid">
                <span className="font-mono-data text-sm">
                  {b.last_paid_at
                    ? new Date(b.last_paid_at).toLocaleDateString()
                    : "—"}
                </span>
              </StatTile>
            </div>

            <div className="flex items-center justify-between bg-[#f0f4ff] border border-[#c0d0ff] rounded-2xl px-4 py-3">
              <div className="flex-1 min-w-0 pr-3">
                <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#002fa7] mb-0.5">
                  Sync from Stripe
                </div>
                <p className="text-xs text-[#5a5a65] leading-relaxed">
                  Re-check pending Stripe sessions and apply any successful payments. Use this if a merchant paid but their status didn't update.
                </p>
              </div>
              <Button
                data-testid="sync-stripe-btn"
                size="sm"
                disabled={busy}
                onClick={syncStripe}
                className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white whitespace-nowrap"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
                Sync
              </Button>
            </div>

            <div className="bg-[#f9f9fb] border border-[#e5e5eb] rounded-2xl p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65]">
                Manual bypass {b.manual_bypass && <span className="text-[#1a1a8a]">· active</span>}
              </div>
              <p className="text-sm text-[#5a5a65]">
                Use this when the merchant pays you in cash, ACH, or wire. Bypassed
                businesses stay fully active regardless of Stripe status.
              </p>
              <Input
                data-testid="bypass-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={b.manual_bypass ? (b.bypass_note || "Reason / note") : "Reason (e.g. paid in cash 2026-05)"}
                className="h-10 rounded-xl border-[#e5e5eb]"
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="toggle-bypass-btn"
                  size="sm"
                  disabled={busy}
                  onClick={toggleBypass}
                  className={
                    b.manual_bypass
                      ? "rounded-full bg-[#5a5a65] hover:bg-[#3a3a45] text-white"
                      : "rounded-full bg-[#1a1a8a] hover:bg-[#101060] text-white"
                  }
                >
                  {b.manual_bypass ? "Remove bypass" : "Enable bypass"}
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                  <Input
                    data-testid="mark-paid-months-input"
                    type="number"
                    min="1"
                    value={months}
                    onChange={(e) => setMonths(e.target.value)}
                    className="h-10 w-20 rounded-xl border-[#e5e5eb] font-mono-data text-center"
                  />
                  <span className="text-sm text-[#5a5a65]">months</span>
                  <Button
                    data-testid="mark-paid-btn"
                    size="sm"
                    disabled={busy}
                    onClick={markPaid}
                    className="rounded-full bg-[#002fa7] hover:bg-[#002585] text-white"
                  >
                    Mark paid
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-dashed border-[#e5e5eb] -mt-1">
                <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] pt-2">
                  Days left override
                </div>
                <div className="flex items-center gap-2 ml-auto pt-2">
                  <Input
                    data-testid="set-days-left-input"
                    type="number"
                    min="0"
                    max="3650"
                    placeholder={b.days_left ?? 0}
                    value={daysLeftDraft}
                    onChange={(e) => setDaysLeftDraft(e.target.value)}
                    className="h-10 w-24 rounded-xl border-[#e5e5eb] font-mono-data text-center"
                  />
                  <span className="text-sm text-[#5a5a65]">days</span>
                  <Button
                    data-testid="set-days-left-btn"
                    size="sm"
                    disabled={busy || daysLeftDraft === ""}
                    onClick={setDaysLeft}
                    variant="outline"
                    className="rounded-full border-[#002fa7]/40 text-[#002fa7] hover:bg-[#002fa7]/10"
                  >
                    Set days
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-[#5a5a65] leading-relaxed -mt-1">
                Use <span className="font-semibold">Set days</span> when Stripe already charged the merchant on auto-renew
                but our record didn't advance. It rewrites the current period end to today + N days.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2">
                Payment history
              </div>
              {txns.length === 0 ? (
                <div className="text-sm text-[#5a5a65] italic">No payments yet.</div>
              ) : (
                <div className="border border-[#e5e5eb] rounded-xl divide-y divide-[#e5e5eb] max-h-48 overflow-auto">
                  {txns.map((t) => (
                    <div key={t.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <div className="font-semibold">
                          ${Number(t.amount || 0).toFixed(2)}{" "}
                          <span className="text-[11px] font-normal text-[#5a5a65] uppercase">
                            {t.method === "cash_or_ach" ? "Manual" : "Stripe"}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#5a5a65]">
                          {new Date(t.created_at).toLocaleString()}
                          {t.note ? ` · ${t.note}` : ""}
                        </div>
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${
                          t.payment_status === "paid"
                            ? "bg-[#dcf6e2] text-[#1a6b34]"
                            : "bg-[#fff4d6] text-[#7a5a00]"
                        }`}
                      >
                        {t.payment_status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full border-[#e5e5eb]">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, children }) {
  return (
    <div className="bg-white border border-[#e5e5eb] rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5a5a65] font-semibold mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative px-4 py-3 text-sm font-semibold transition-colors ${
        active
          ? "text-[#002fa7]"
          : "text-[#5a5a65] hover:text-[#0a0a0a]"
      }`}
    >
      <span className="flex items-center">{children}</span>
      {active && (
        <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-[#002fa7]" />
      )}
    </button>
  );
}

function SupportTicketsView({ tickets, loading, onReload }) {
  const [expanded, setExpanded] = useState(null);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/admin/support-tickets/${id}/status`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[#002fa7] mb-2">
            Support tickets
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[0.95]">
            {tickets.length} {tickets.length === 1 ? "request" : "requests"}.
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={onReload}
          className="rounded-full border-[#e5e5eb]"
          data-testid="reload-tickets-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[#5a5a65] py-20">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="bg-white border border-[#e5e5eb] rounded-2xl p-16 text-center">
          <div className="w-12 h-12 rounded-full bg-[#f0f0f4] flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-6 h-6 text-[#002fa7]" />
          </div>
          <h3 className="font-display text-2xl font-extrabold mb-2">No tickets yet</h3>
          <p className="text-[#5a5a65]">
            When someone submits a request through the chat widget, it'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const isOpen = expanded === t.id;
            return (
              <div
                key={t.id}
                data-testid={`ticket-row-${t.id}`}
                className={`bg-white border rounded-2xl overflow-hidden transition-all ${
                  t.status === "resolved" ? "border-[#e5e5eb] opacity-60" : "border-[#e5e5eb]"
                }`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : t.id)}
                  className="w-full p-4 flex items-center gap-4 text-left hover:bg-[#f9f9fb] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-display font-extrabold text-lg">
                        {t.name || "(no name)"}
                      </span>
                      {t.status === "open" ? (
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-[#fde0e0] text-[#a01010] rounded-full px-2 py-0.5">
                          Open
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-[#dcf6e2] text-[#1a6b34] rounded-full px-2 py-0.5">
                          Resolved
                        </span>
                      )}
                      {t.business_name && (
                        <span className="text-xs text-[#5a5a65]">· {t.business_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-xs text-[#5a5a65]">
                      {t.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {t.email}
                        </span>
                      )}
                      {t.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {t.phone}
                        </span>
                      )}
                      {t.address && (
                        <span className="text-xs text-[#5a5a65]">· {t.address}</span>
                      )}
                      <span className="font-mono-data">
                        {new Date(t.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-[#5a5a65] flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 pt-2 border-t border-[#e5e5eb] bg-[#f9f9fb] space-y-3">
                    {t.message && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-1">
                          Their message
                        </div>
                        <div className="text-sm bg-white border border-[#e5e5eb] rounded-xl p-3 whitespace-pre-wrap">
                          {t.message}
                        </div>
                      </div>
                    )}
                    {t.transcript && t.transcript.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-1">
                          Full chat transcript
                        </div>
                        <div className="bg-white border border-[#e5e5eb] rounded-xl p-3 max-h-64 overflow-y-auto space-y-2">
                          {t.transcript.map((m, i) => (
                            <div key={i} className="text-sm">
                              <div className="text-[10px] uppercase tracking-wider font-bold text-[#5a5a65] mb-0.5">
                                {m.user ? "User" : "AI"}
                              </div>
                              <div className="whitespace-pre-wrap">
                                {m.user || m.ai}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap pt-1">
                      {t.email && (
                        <a
                          href={`mailto:${t.email}?subject=${encodeURIComponent("Re: your SparkLoop request")}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#002fa7] hover:bg-[#001a55] text-white text-xs font-semibold"
                        >
                          <Mail className="w-3.5 h-3.5" /> Reply via email
                        </a>
                      )}
                      {t.phone && (
                        <a
                          href={`tel:${t.phone}`}
                          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#e5e5eb] text-xs font-semibold hover:bg-white"
                        >
                          <Phone className="w-3.5 h-3.5" /> Call
                        </a>
                      )}
                      <button
                        data-testid={`ticket-toggle-status-${t.id}`}
                        onClick={() => updateStatus(t.id, t.status === "open" ? "resolved" : "open")}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#e5e5eb] text-xs font-semibold hover:bg-white"
                      >
                        {t.status === "open" ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Mark resolved
                          </>
                        ) : (
                          "Reopen"
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
